const DATE_ONLY_PATTERN: RegExp = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_MIDNIGHT_PATTERN: RegExp = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(\.000)?Z$/;

/**
 * Parses inventory-style calendar dates in the browser's local timezone.
 *
 * Date-only strings (`YYYY-MM-DD`) and UTC-midnight instants (`…T00:00:00Z`) are
 * treated as calendar days, not UTC timestamps (avoids Dec 31 in US timezones).
 *
 * @param raw - API string, Date, or nullish value.
 * @returns Local midnight on that calendar day, or `null` when missing or invalid.
 */
export function parseLocalCalendarDate(raw: string | Date | null | undefined): Date | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime()) || raw.getFullYear() <= 1) {
      return null;
    }
    if (
      raw.getUTCHours() === 0 &&
      raw.getUTCMinutes() === 0 &&
      raw.getUTCSeconds() === 0 &&
      raw.getUTCMilliseconds() === 0
    ) {
      return localMidnightFromParts(raw.getUTCFullYear(), raw.getUTCMonth() + 1, raw.getUTCDate());
    }
    return raw;
  }

  const trimmed: string = raw.trim();
  const dateOnlyMatch: RegExpExecArray | null = DATE_ONLY_PATTERN.exec(trimmed);
  if (dateOnlyMatch) {
    return localMidnightFromParts(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]),
      Number(dateOnlyMatch[3])
    );
  }

  const utcMidnightMatch: RegExpExecArray | null = UTC_MIDNIGHT_PATTERN.exec(trimmed);
  if (utcMidnightMatch) {
    return localMidnightFromParts(
      Number(utcMidnightMatch[1]),
      Number(utcMidnightMatch[2]),
      Number(utcMidnightMatch[3])
    );
  }

  const parsed: Date = new Date(trimmed);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 1) {
    return null;
  }
  return parsed;
}

/**
 * @param year - Calendar year.
 * @param month - Calendar month (1–12).
 * @param day - Calendar day (1–31).
 */
function localMidnightFromParts(year: number, month: number, day: number): Date | null {
  const date: Date = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date.getFullYear() <= 1 ? null : date;
}
